import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import useToastNotification from '../useToastNotification'
import { SupportRequestAddItem } from '../../utils/types/supportTypes'
import {
  validateSupportImages,
  validateSupportRequestForm,
} from '../../utils/validations/supportValidations'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SUPPORT_REQUESTS_LIST_QUERY_KEY } from './useFetchSupportRequests'
import { uploadSupportRequestImages } from './useAddSupportRequestImages'

type AddSupportRequestParams = {
  supportRequest: SupportRequestAddItem
  fnSuccess?: () => void
}

type AddSupportRequestMutationProps = {
  params: AddSupportRequestParams
}

const useAddSupportRequest = () => {
  const axiosPrivate = useAxiosPrivate()
  const queryClient = useQueryClient()
  const { sendToastMessage, showApiErrorMessage } = useToastNotification()

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ params }: AddSupportRequestMutationProps) => {
      const { supportRequest } = params

      const response = await axiosPrivate.post('/api/support-requests', {
        reason: supportRequest.reason,
        subject: supportRequest.subject.trim(),
        body: supportRequest.body.trim(),
        sharedWithOrganisation: supportRequest.sharedWithOrganisation,
      })

      if (supportRequest.images.length === 0) {
        return { imagesFailed: false }
      }

      // The request already exists at this point, so a failed upload must not
      // surface as a failed request - the images can be added again from its page.
      try {
        await uploadSupportRequestImages(
          axiosPrivate,
          response.data.id,
          supportRequest.images
        )
        return { imagesFailed: false }
      } catch {
        return { imagesFailed: true }
      }
    },
    onSuccess: ({ imagesFailed }, variables) => {
      queryClient.invalidateQueries({
        queryKey: SUPPORT_REQUESTS_LIST_QUERY_KEY,
      })

      variables.params.fnSuccess?.()

      if (imagesFailed) {
        sendToastMessage(
          'warning',
          `Support request sent, but some images couldn't be uploaded. You can add them from the request page.`
        )
        return
      }

      sendToastMessage(
        'success',
        `Support request sent successfully. We will get back to you as soon as possible.`
      )
    },
    onError: (error) => {
      showApiErrorMessage(
        error,
        'Something went wrong while sending your support request. Please try again.'
      )
    },
  })

  const handleAddSupportRequest = (params: AddSupportRequestParams) => {
    const validationError =
      validateSupportRequestForm(params.supportRequest) ??
      validateSupportImages(params.supportRequest.images, 0)

    if (validationError !== undefined) {
      sendToastMessage('error', validationError)
      return
    }

    mutate({ params })
  }

  return { isPending, handleAddSupportRequest } as const
}

export default useAddSupportRequest
